import { GoogleAnalyticsService } from 'core/services/GoogleAnalyticsService';
import { TFunction } from 'i18next';
import { action, computed, makeObservable, observable } from 'mobx';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { RegisterOrLoginStore } from './step_1-user-info/RegisterOrLogin.store';
import { ConfirmCodeStore } from './step_2-confirm-code';
import { ResumeChooserStore } from './step_3-resume-selection';
import { WithoutResumeStore } from './step_4-apply_by/WithoutResume.store';

export type ApplyStatus = 'success' | 'error' | 'warning' | 'pending';
export class ApplyJobStore {
   private readonly applyJobState = new AsyncState<any>();

   readonly registerOrLogin: RegisterOrLoginStore;
   readonly confirmCodeStore: ConfirmCodeStore;
   readonly resumeChooserStore: ResumeChooserStore;
   readonly withoutResumeStore: WithoutResumeStore;

   status: ApplyStatus = 'pending';

   constructor() {
      makeObservable(this, {
         status: observable,
         setStatus: action,
         submitting: computed,
         isPhoneConfirmed: computed,
         checkUserMessage: computed,
      });
      this.registerOrLogin = new RegisterOrLoginStore();
      this.resumeChooserStore = new ResumeChooserStore();
      this.confirmCodeStore = new ConfirmCodeStore(this);
      this.withoutResumeStore = new WithoutResumeStore(this);
   }

   setStatus(s: ApplyStatus) {
      this.status = s;
   }

   get submitting() {
      return this.applyJobState.status === 'pending';
   }

   get isPhoneConfirmed(): boolean {
      return (
         !!this.registerOrLogin.form.controls['contact'].value &&
         !this.registerOrLogin.form.controls['email_address'].value
      );
   }

   get checkUserMessage(): string {
      return this.registerOrLogin.successMessage;
   }

   async apply(
      dto: {
         variant: 'cv' | 'no-resume';
         idJob: string | number;
         subscribeAlert: boolean;
         first_name: string;
         last_name: string;
         contact?: string | null;
         email_address?: string | null;
         city_id?: number | null;
         birthday?: string | null;
      },
      {
         onSuccess,
         onError,
         t,
      }: {
         t?: TFunction<'global', string>;
         onSuccess?: (res: any) => void;
         onError?: (err?: any) => void;
      } = {}
   ): Promise<void> {
      console.log(this.resumeChooserStore.file, this.resumeChooserStore.selectedResume);

      GoogleAnalyticsService.trackEvent('job_apply', {
         message: 'Resume start submit',
         status: 'INFO',
      });

      const formData = new FormData();
      formData.append('idJob', dto.idJob?.toString());
      formData.append('first_name', dto.first_name);
      formData.append('last_name', dto.last_name);
      formData.append('message', this.resumeChooserStore.message);

      if (dto.subscribeAlert) {
         formData.append('subscribeAlert', 'true');
      }
      if (dto.email_address) {
         formData.append('email_address', dto.email_address);
      }
      if (dto.contact) {
         formData.append('contact', dto.contact);
      }

      if (dto.variant === 'no-resume') {
         const cityId = dto.city_id || this.withoutResumeStore.form.controls['city_id'].value;
         const birthday = dto.birthday || this.withoutResumeStore.form.controls['birthday'].value;
         formData.append('city_id', cityId?.toString());
         formData.append('birthday', birthday);
      }

      if(dto.variant === 'cv') {
          const file = this.resumeChooserStore.file;
          const resume = this.resumeChooserStore.selectedResume;
          if (file) {
             formData.append('file', file);
          }
          if (resume) {
             if ('file' in resume) {
                formData.append('fileId', resume.file_id?.toString());
             } else {
                formData.append('idCv', resume.id.toString());
             }
          }
      }
     

      await this.applyJobState.run(
         () =>
            HttpClient.post('/jobApply/sentJobApply', formData, {
               headers: {
                  'Content-Type': 'multipart/form-data',
               },
            }),
         {
            onSuccess: (res) => {
               if (res.status === 200) {
                  GoogleAnalyticsService.trackEvent('job_apply', {
                     message: res.message,
                     status: 'SUCCESS',
                  });
                  this.setStatus('success');
               } else if (res.status === 500) {
                  GoogleAnalyticsService.trackEvent('job_apply', {
                     message: t?.('jobDescription.jobAlreadyApplied'),
                     status: 'WARNING',
                  });
                  this.setStatus('warning');
               } else {
                  GoogleAnalyticsService.trackEvent('job_apply', {
                     message: t?.('jobDescription.applyError'),
                     status: 'ERROR',
                  });
                  this.setStatus('error');
               }
               onSuccess?.(res);
            },
            onError: () => {
               GoogleAnalyticsService.trackEvent('job_apply', {
                  message: t?.('jobDescription.applyError'),
                  status: 'ERROR',
               });
               onError?.();
            },
         }
      );
   }
}
